// src/features/mech/casting/playbook/CastingPlayBook.jsx
// Casting Molding-specific PlayBook configuration component
import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import PerlSlider from "../../../../shared/components/PerlSlider";
import PerlAutocomplete from "../../../../shared/components/PerlAutocomplete";
import { theme } from "../../../../design/theme";
const { perlColors } = theme.custom;
/**
 * CastingPlayBook - Casting Molding-specific playbook configuration
 * 
 * @param {Object} props
 * @param {Object} props.data - PlayBook data object with costParams: { cavities, cycleTime, machineTonnage, ohpRate, runnerType }
 * @param {Function} props.onChange - Callback: (field, value) => void
 */
export default function CastingPlayBook({ data = {}, onChange }) {
  // Extract costParams from data.costParams or data directly (for backward compatibility)
  const costParams = data.costParams || {
    cavities: data.cavities || 0,
    cycleTime: data.cycleTime || 0,
    machineTonnage: data.machineTonnage || 0,
    ohpRate: data.ohpRate || 0,
    runnerType: data.runnerType || "N/A",
  };
  // Handler for parameter changes - updates costParams structure
  const handleParameterChange = (paramName, value) => {
    if (onChange) {
      // Update the costParams nested structure
      onChange("costParams", {
        ...costParams,
        [paramName]: value,
      });
    }
  };
  // First row sliders (2 sliders: Cycle Time, Machine Tonnage)
  const firstRowSliders = [
    {
      key: "cycleTime",
      label: "Cycle Time",
      value: parseFloat(costParams.cycleTime) || 0,
      min: 0,
      max: 1000,
      step: 5,
      unit: " seconds",
      onChange: (e, v) => handleParameterChange("cycleTime", v),
    },
    {
      key: "machineTonnage",
      label: "Machine Tonnage",
      value: parseInt(costParams.machineTonnage) || 0,
      min: 0,
      max: 500,
      step: 10,
      unit: " tons",
      onChange: (e, v) => handleParameterChange("machineTonnage", v),
    },
  ];
  // Runner type options
  const runnerTypeOptions = ["Hot Runner", "Cold Runner", "N/A"];
  return (
    <Box>
      {/* Process Parameters Title */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Process Parameters
      </Typography>
      {/* First Row - 2 sliders (Cycle Time, Machine Tonnage) */}
      <Box
        sx={{
          backgroundColor: perlColors.surface,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {firstRowSliders.map((slider) => (
            <Box
              key={slider.key}
              sx={{
                minWidth: 220,
                flex: "1 1 280px",
                maxWidth: "calc((100% - 24px) / 2)",
                mb: 2,
              }}
            >
              <PerlSlider
                label={slider.label}
                value={slider.value}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                unit={slider.unit}
                onChange={slider.onChange}
              />
            </Box>
          ))}
        </Box>
        {/* Second Row - OHP Rate and Runner Type */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {/* OHP Rate Slider */}
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlSlider
              label="OHP Rate"
              value={costParams.ohpRate ? parseFloat(parseFloat(costParams.ohpRate).toFixed(2)) : 0}
              min={0}
              max={30}
              step={0.5}
              unit="%"
              onChange={(e, v) => handleParameterChange("ohpRate", parseFloat(v.toFixed(2)))}
            />
          </Box>
          {/* Runner Type Dropdown */}
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            {/* <PerlAutocomplete
              label="Runner Type"
              value={costParams.runnerType || "N/A"}
              onChange={(_, newValue) => {
                const runnerValue = newValue || "N/A";
                handleParameterChange("runnerType", runnerValue);
              }}
              options={runnerTypeOptions}
              placeholder="Select runner type"
            /> */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}